import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import * as authApi from '@/api/auth'
import { hasProblemCode, isAbortError } from '@/api/problem'
import type { LoginRequest, Permission, User } from '@/types/api'

/**
 * 会话状态。
 *
 * 后端的会话是 HttpOnly Cookie（`__Host-session`），**前端读不到它** ——
 * 所以「是否登录」只能靠 `GET /me`：200 就是登录，401 就是游客。401 在这里
 * 是正常结果而不是错误，页面不该为此弹红条。
 *
 * 同时保存 `/me` 的 ETag：修改显示名是版本化写操作，必须带 `If-Match`，
 * 缺失会 428、过期会 412。把 ETag 和用户对象放在一起，调用方就不会漏。
 *
 * CSRF Token 不在这里管：登录/登出的响应各自带一份新 Token，由 `api/auth.ts`
 * 直接采纳（后端为此让这两个响应返回 200 而不是 204）。
 *
 * ## 这里**没有** `register`
 *
 * `POST /auth/registrations` 只是「发出一封信」：它既不建账号也不建会话，
 * 成功之后用户仍是游客。所以它与会话状态无关，注册页直接调 `authApi.register`，
 * 这个 Provider 只保留真正会改变会话的能力。真正建立会话的是三条
 * `*-confirmations`，它们的收尾走 `adoptSession`。
 */

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous'

interface AuthContextValue {
  status: AuthStatus
  user: User | null
  /** 能进后台（`admin` 或 `superadmin`）。**不代表能碰任何模块**，见 `can`。 */
  isAdmin: boolean
  /**
   * 超级管理员。
   *
   * 三类能力只看这个，不看 `can`：改角色/冻结/删账号、授予与回收权限、审计日志。
   * 它们刻意不是权限位，所以 `can('audit')` 这种写法根本不存在。
   */
  isSuperAdmin: boolean
  /**
   * 是否持有某个细分权限。
   *
   * 超管恒为 `true`，因为后端在 `User.permissions` 里给他返回的是全量清单 ——
   * 这一层不需要为角色写分支。
   *
   * 这是**界面门控**，不是安全边界：真正的门在后端，每条 `/admin/**` 都过
   * `requirePermission` 或 `requireSuperAdmin`。这里的作用是不给人展示一个点下去
   * 必然 403 的按钮。
   */
  can: (permission: Permission) => boolean
  login: (payload: LoginRequest) => Promise<User>
  /**
   * 采纳一个**已经由后端建立**的会话。三条 `*-confirmations` 的收尾。
   * 它不发登录请求 —— 那里没有口令可用，而会话已经有了。
   */
  adoptSession: (next: User) => void
  logout: () => Promise<void>
  updateDisplayName: (displayName: string) => Promise<User>
  /** 收藏等操作 401 之后调用，让界面回到游客态。 */
  markAnonymous: () => void
  refresh: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [user, setUser] = useState<User | null>(null)
  const [etag, setEtag] = useState<string | undefined>(undefined)
  const [reloadToken, setReloadToken] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    authApi
      .fetchCurrentUser(controller.signal)
      .then(({ user: current, etag: currentEtag }) => {
        setUser(current)
        setEtag(currentEtag)
        setStatus('authenticated')
      })
      .catch((error: unknown) => {
        if (isAbortError(error)) return
        // 401 = 游客，是正常状态。其他错误（网络、500）也只能按游客渲染，
        // 但不该把用户挡在只读页面之外，所以同样落到 anonymous。
        if (!hasProblemCode(error, 'authentication_required')) {
          console.warn('[auth] 获取当前用户失败，按游客处理', error)
        }
        setUser(null)
        setEtag(undefined)
        setStatus('anonymous')
      })
    return () => controller.abort()
  }, [reloadToken])

  const refresh = useCallback(() => setReloadToken((token) => token + 1), [])

  const login = useCallback(async (payload: LoginRequest) => {
    const next = await authApi.login(payload)
    setUser(next)
    setStatus('authenticated')
    // 登录响应不带 ETag；改显示名前需要一次 `GET /me` 拿到它。
    setEtag(undefined)
    setReloadToken((token) => token + 1)
    return next
  }, [])

  /**
   * 采纳一个已经由后端建立的会话。
   *
   * 三条 `*-confirmations`（确认注册 / 重设口令 / 确认新邮箱）与登录同形：
   * Cookie 已经写好，响应体里是用户与一份新 CSRF Token（后者由 `api/auth.ts`
   * 采纳）。所以这里不能走 `login()` —— 没有口令可用，而会话已经有了。
   *
   * 做的事与 `login` 的收尾完全一样，包括那次 `reloadToken` 递增：确认响应
   * 与登录响应一样不带 ETag，而改显示名需要它，所以要补一次 `GET /me`。
   */
  const adoptSession = useCallback((next: User) => {
    setUser(next)
    setStatus('authenticated')
    setEtag(undefined)
    setReloadToken((token) => token + 1)
  }, [])

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } finally {
      // 无论后端怎么回，本地都要回到游客态：会话已经不可信了。
      setUser(null)
      setEtag(undefined)
      setStatus('anonymous')
    }
  }, [])

  const updateDisplayName = useCallback(
    async (displayName: string) => {
      // 没有 ETag 就先取一次，否则后端会 428 precondition_required。
      let currentEtag = etag
      if (!currentEtag) {
        const fresh = await authApi.fetchCurrentUser()
        currentEtag = fresh.etag
        setUser(fresh.user)
      }
      if (!currentEtag) {
        throw new Error('无法获取账号版本号，请刷新页面后重试。')
      }
      const { user: next, etag: nextEtag } = await authApi.updateDisplayName(
        displayName,
        currentEtag,
      )
      setUser(next)
      setEtag(nextEtag)
      return next
    },
    [etag],
  )

  const markAnonymous = useCallback(() => {
    setUser(null)
    setEtag(undefined)
    setStatus('anonymous')
  }, [])

  const value = useMemo<AuthContextValue>(() => {
    const role = user?.role
    // 后端恒返回数组（`[]` 而不是 null），但旧版本后端不带这个字段，
    // 而"字段缺失"在这里必须退化成"什么都不能做"而不是"什么都能做"。
    const granted = new Set<Permission>(user?.permissions ?? [])
    const isAdmin = role === 'admin' || role === 'superadmin'
    return {
      status,
      user,
      isAdmin,
      isSuperAdmin: role === 'superadmin',
      can: (permission: Permission) => isAdmin && granted.has(permission),
      login,
      adoptSession,
      logout,
      updateDisplayName,
      markAnonymous,
      refresh,
    }
  }, [status, user, login, adoptSession, logout, updateDisplayName, markAnonymous, refresh])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth 必须在 <AuthProvider> 内使用')
  return context
}
